import {
  kpiDelta,
  onBoardMinutes,
  quantile,
  vehiclesUsed,
  waitMinutes,
  type KpiDirection,
} from '../../../../../services/simulation/runReport';
import type { SimulationRunSummary } from '../../../../../types/simulation';
import type { RunReportDerived } from '../useRunReport';

export type KpiId = 'success' | 'requests' | 'wait' | 'trip' | 'vehicles';

/** Every KPI except vehicles used comes from `/metrics`; vehicles used comes from activities and vehicles. */
export type KpiSource = 'metrics' | 'derived';

export interface KpiView {
  readonly id: KpiId;
  readonly source: KpiSource;
  readonly digits: number;
  readonly unitKey: string | null;
  readonly value: number | null;
  readonly delta: ReturnType<typeof kpiDelta>;
  readonly sub: Readonly<Record<string, number>> | null;
}

type Metrics = Partial<SimulationRunSummary> | null;

interface KpiSpec {
  readonly source: KpiSource;
  readonly direction: KpiDirection;
  readonly digits: number;
  readonly unitKey: string | null;
}

const SPECS: Readonly<Record<KpiId, KpiSpec>> = {
  success: { source: 'metrics', direction: 'higher_is_better', digits: 1, unitKey: 'results.report.units.percent' },
  requests: { source: 'metrics', direction: 'neutral', digits: 0, unitKey: null },
  wait: { source: 'metrics', direction: 'lower_is_better', digits: 1, unitKey: 'results.report.units.minutes' },
  trip: { source: 'metrics', direction: 'lower_is_better', digits: 1, unitKey: 'results.report.units.minutes' },
  vehicles: { source: 'derived', direction: 'neutral', digits: 0, unitKey: null },
};

const percent = (ratio: number | undefined) => (ratio == null ? null : ratio * 100);

function p90(values: readonly (number | null)[]): number | null {
  return quantile(values.filter((value): value is number => value != null), 0.9);
}

function round(value: number | null, digits: number): number | null {
  return value == null ? null : Number(value.toFixed(digits));
}

/** Deltas are taken between the shown, rounded values, so a difference too small to show reads as no change. */
function kpi(id: KpiId, value: number | null, compare: number | null, sub: KpiView['sub']): KpiView {
  const spec = SPECS[id];
  const shown = round(value, spec.digits);
  const delta = kpiDelta(shown, round(compare, spec.digits), spec.direction);
  return { id, ...spec, value: shown, delta: delta && { ...delta, delta: Number(delta.delta.toFixed(spec.digits)) }, sub };
}

export function buildKpis(metrics: Metrics, compare: Metrics, derived: RunReportDerived | null): KpiView[] {
  const total = metrics?.no_rides;
  const matched = metrics?.matched_rides;
  const unmatched = metrics?.reject_tally;
  const waitP90 = derived ? p90(derived.rides.map(waitMinutes)) : null;
  const tripP90 = derived ? p90(derived.rides.map(onBoardMinutes)) : null;
  return [
    kpi('success', percent(metrics?.success_ratio), percent(compare?.success_ratio), matched != null && total != null ? { matched, total } : null),
    kpi('requests', total ?? null, compare?.no_rides ?? null, unmatched != null ? { unmatched } : null),
    kpi('wait', metrics?.waiting_time_min_median ?? null, compare?.waiting_time_min_median ?? null, waitP90 != null ? { p90: waitP90 } : null),
    kpi('trip', metrics?.median_time_on_vehicle ?? null, compare?.median_time_on_vehicle ?? null, tripP90 != null ? { p90: tripP90 } : null),
    kpi('vehicles', derived ? vehiclesUsed(derived.shifts) : null, null, derived ? { fleet: derived.shifts.length } : null),
  ];
}
